import {ElementRef, inject, Injectable, QueryList} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {SlideshowStateService} from './slideshow-state.service';

@Injectable({
  providedIn: 'root'
})
export class VideoControlService {

  private slideshowStateService = inject(SlideshowStateService);

  // Video elements reference (set from component)
  private videoElements: QueryList<ElementRef<HTMLVideoElement>> | null = null;

  // Current media index (set from component)
  private _currentMediaIndex = new BehaviorSubject<number>(0);

  /**
   * Set video elements reference from the component
   */
  setVideoElements(elements: QueryList<ElementRef<HTMLVideoElement>>): void {
    this.videoElements = elements;
  }

  /**
   * Set current media index
   */
  setCurrentMediaIndex(index: number): void {
    this._currentMediaIndex.next(index);
  }

  /**
   * Get current media index
   */
  getCurrentMediaIndex(): number {
    return this._currentMediaIndex.value;
  }

  /**
   * Stop all video elements
   */
  stopAllVideos(): void {
    if (!this.videoElements) return;

    this.videoElements.forEach((videoRef, index) => {
      const video = videoRef.nativeElement;
      if (!video.paused) {
        video.pause();
        console.log(`Stopped video at index ${index}`);
      }
    });

    this.slideshowStateService.setVideoPlaying(false);
  }

  /**
   * Get the current video element
   */
  getCurrentVideo(): HTMLVideoElement | null {
    if (!this.videoElements) return null;

    const currentIndex = this.getCurrentMediaIndex();
    const videoElements = this.videoElements.toArray();

    if (videoElements.length > currentIndex) {
      return videoElements[currentIndex].nativeElement;
    }

    return null;
  }

  /**
   * Pause the current video if it's playing
   */
  pauseCurrentVideo(): void {
    const currentVideo = this.getCurrentVideo();
    if (currentVideo && !currentVideo.paused) {
      currentVideo.pause();
      console.log('Paused current video');
    }
  }

  /**
   * Play the current video if it's paused
   */
  playCurrentVideo(): void {
    const currentVideo = this.getCurrentVideo();
    if (currentVideo && currentVideo.paused) {
      currentVideo.play().catch(error => {
        console.error('Error playing current video:', error);
      });
      console.log('Resumed current video');
    }
  }

  /**
   * Play a specific video by index
   */
  async playVideoByIndex(index: number): Promise<void> {
    if (!this.videoElements) {
      console.warn('Video elements not available, skipping video play');
      return;
    }

    const videoElements = this.videoElements.toArray();

    if (videoElements.length > index) {
      const videoElement = videoElements[index].nativeElement;
      console.log('Playing video at index:', index);

      try {
        await videoElement.play();
        console.log('Video playback started successfully');
        this.slideshowStateService.setVideoPlaying(true);
      } catch (error) {
        console.error('Error playing video:', error);

        // Try with muted fallback for autoplay restrictions
        if ((error as any).name === 'NotAllowedError') {
          console.log('Attempting to play muted as fallback...');
          videoElement.muted = true;

          try {
            await videoElement.play();
            console.log('Video playback started successfully (muted)');
            this.slideshowStateService.setVideoPlaying(true);
          } catch (secondError) {
            console.error('Error playing muted video:', secondError);
            throw secondError;
          }
        } else {
          throw error;
        }
      }
    } else {
      console.error('Video element not found for index:', index);
      throw new Error(`Video element not found for index: ${index}`);
    }
  }

  /**
   * Handle video ended event
   */
  onVideoEnded(index: number, onAdvanceCallback: () => void): void {
    console.log(`Video at index ${index} ended, advancing to next media`);
    this.slideshowStateService.setVideoPlaying(false);
    onAdvanceCallback();
  }

  /**
   * Handle video metadata loaded event
   */
  onVideoMetadataLoaded(
    event: Event,
    index: number,
    mediaItems: { url: string; type: 'image' | 'video'; duration?: number }[],
    onMediaItemsUpdate: (items: { url: string; type: 'image' | 'video'; duration?: number }[]) => void,
    onLoopRequirementUpdate: () => void
  ): void {
    const video = event.target as HTMLVideoElement;

    if (video && !isNaN(video.duration)) {
      const updatedMediaItems = [...mediaItems];

      if (index < updatedMediaItems.length) {
        // Update the video duration in our media array
        updatedMediaItems[index].duration = video.duration;
        onMediaItemsUpdate(updatedMediaItems);

        // Recalculate audio loop requirements with the new duration
        onLoopRequirementUpdate();

        console.log(`Video at index ${index} duration loaded: ${video.duration}s`);
      }
    }
  }

  /**
   * Handle video can play event
   */
  onVideoCanPlay(event: Event, index: number): void {
    const video = event.target as HTMLVideoElement;
    console.log(`Video at index ${index} can play now:`, video.src);

    // Only update playing state if this is the current video
    if (index === this.getCurrentMediaIndex()) {
      this.slideshowStateService.setVideoPlaying(true);
    }
  }

  /**
   * Handle video error event
   */
  onVideoError(event: Event, index: number, onAdvanceCallback: () => void): void {
    const video = event.target as HTMLVideoElement;
    console.error(`Video at index ${index} error occurred:`, video.error);

    // Only advance if this is the current video
    if (index === this.getCurrentMediaIndex()) {
      // If video fails to play, move to the next media item
      this.slideshowStateService.setVideoPlaying(false);
      onAdvanceCallback();
    }
  }

  /**
   * Check if current media is a video
   */
  isCurrentMediaVideo(mediaItems: { url: string; type: 'image' | 'video' }[]): boolean {
    const currentIndex = this.getCurrentMediaIndex();
    return currentIndex < mediaItems.length && mediaItems[currentIndex].type === 'video';
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this.stopAllVideos();
    this._currentMediaIndex.next(0);
    this.videoElements = null;
  }
} 
